import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { GenericHeaderBackButton } from 'src/consumer/components/ActivityPlansPage/MyPlanComponents'
import { useScreenSize } from 'src/customHooks/customHooks'
import AssessmentAccessSettings from 'src/mentor/components/MentorSettingsPage/components/AssessmentAccessSettings'
import { AppointmentCalendarSettings } from 'src/mentor/components/MentorSettingsPage/components/AppointmentCalendarSettings'
import PractitionerAssessmentAccessView from 'src/mentor/components/MentorSettingsPage/components/PractitionerAssessmentAccessView'
import { EMRSections } from 'src/mentor/components/MentorSettingsPage/components/EmrSections'
import PrescriptionViewSettings, {
  PRESCRIPTION_VIEW_SETTINGS_TYPE,
} from 'src/mentor/components/MentorSettingsPage/components/PrescriptionViewSettings/PrescriptionViewSettings'
import SupportOnlyDoctorSettings from 'src/mentor/components/MentorSettingsPage/components/SupportOnlyDoctorSettings/SupportOnlyDoctorSettings'
import { ERMOptionsSyncComponent } from 'src/mentor/components/MentorSettingsPage/MentorSettingsComponents'
import {
  MPSettingsListContainer,
  MPSettingsListItem,
  MPSettingsSectionHeader,
} from 'src/mentor/components/MentorSettingsPage/styles'
import { getUserRoles } from 'src/mentor/layout/utils'

export const EMR_SETTINGS_SECTIONS = {
  emrSections: {
    id: 'emr-sections',
    name: 'EMR Sections',
    accessableTo: ['ROLE_DOCTOR', 'ROLE_THERAPIST'],
  },
  updateLocalData: {
    id: 'update-local-data',
    name: 'Update Local Data',
    accessableTo: ['ROLE_DOCTOR'],
  },
  debug: { id: 'debug', name: 'Debug', accessableTo: ['ROLE_DOCTOR', 'ROLE_THERAPIST'] },
  prescriptionViewSettings: {
    id: 'prescription-view-settings',
    name: 'EMR Editor Settings',
    accessableTo: ['ROLE_DOCTOR', 'ROLE_THERAPIST'],
  },
  printSettings: {
    id: 'print-settings',
    name: 'Print Settings',
    accessableTo: ['ROLE_DOCTOR', 'ROLE_THERAPIST'],
  },
  appointmentCalendarSettings: {
    id: 'appointment-calendar-settings',
    name: 'Appointment Calendar Settings',
    accessableTo: ['ROLE_DOCTOR', 'ROLE_THERAPIST'],
  },
  supportOnlyDoctorSettings: {
    id: 'support-only-doctor-settings',
    name: 'Support Only Doctor Settings',
    accessableTo: [],
  },
  assessmentAccess: {
    id: 'assessment-access',
    name: 'Assessment Access',
    accessableTo: ['ROLE_DOCTOR', 'ROLE_THERAPIST'],
  },
}

export default function MentorSettingsPageMainComponent({
  excludeSections = [],
  mentorClinicAssociationData,
  defaultSelectedSection,
  hideBackButton,
  emrSectionsApiSignature,
  emrSettingsSaveApiSignature,
  prescriptionViewSettingsGetApiSignature,
  prescriptionViewSettingsSaveApiSignature,
  appointmentCalendarSettingsGetApiSignature,
  appointmentCalendarSettingsSaveApiSignature,
  assessmentAccessGetApiSignature,
  assessmentAccessSaveApiSignature,
  practitionerId,
  mentorProfileData,
  forSupportPortal = false,
}) {
  const [selected, setSelected] = useState()
  const { width } = useScreenSize()
  const currentRoles = getUserRoles()
  const AVAILABLE_TABS = Object.values(EMR_SETTINGS_SECTIONS).filter(
    (section) =>
      !excludeSections.includes(section.id) &&
      (section?.accessableTo?.some((role) => currentRoles.includes(role)) || forSupportPortal),
  )

  useEffect(() => {
    if (width > 768) {
      let tabToSet = defaultSelectedSection
      if (tabToSet && !AVAILABLE_TABS.includes(tabToSet)) {
        tabToSet = AVAILABLE_TABS?.[0]?.id
      }
      setSelected(tabToSet)
    }
  }, [])
  return (
    <>
      <MPSettingsSectionHeader>
        {!hideBackButton && (
          <GenericHeaderBackButton
            clickfunction={() => {
              if (width < 768) {
                if (selected) {
                  setSelected()
                } else {
                  window.history.back()
                }
              } else {
                window.history.back()
              }
            }}
            styles={{ margin: '20px' }}
          />
        )}
      </MPSettingsSectionHeader>
      <div 
        className="d-flex" 
        style={{ background: '#fff', flexGrow: 1, display: 'flex !important', flexDirection: 'row' }}
        ref={(el) => {
          if (el) {
            // Force the display style
            el.style.setProperty('display', 'flex', 'important');
            el.style.setProperty('flex-direction', 'row', 'important');
          }
        }}
      >
        <MPSettingsListContainer removableOnSmall={Boolean(selected)}>
          {AVAILABLE_TABS.map((section) => (
            <MPSettingsListItem
              key={section.id}
              selected={selected === section.id}
              onClick={() => setSelected(section.id)}
            >
              {section.name}
            </MPSettingsListItem>
          ))}
        </MPSettingsListContainer>
        {Boolean(selected) && (
          <div style={{ padding: '16px', flex: '1 1 auto', minWidth: 0 }}>
            {/* Local Data Update UI */}
            {selected === EMR_SETTINGS_SECTIONS.updateLocalData.id &&
              !excludeSections.includes(EMR_SETTINGS_SECTIONS.updateLocalData.id) && (
                <div>
                  <div style={{ padding: '32px 8px', width: '100%' }}>
                    <ERMOptionsSyncComponent
                      takeActionsItself={false}
                      mentorClinicAssociationData={mentorClinicAssociationData}
                    />
                  </div>
                </div>
              )}
            {selected === EMR_SETTINGS_SECTIONS.emrSections.id &&
              !excludeSections.includes(EMR_SETTINGS_SECTIONS.emrSections.id) && (
                <div>
                  <div style={{ padding: '32px 8px', width: '100%' }}>
                    <EMRSections
                      mentorProfileData={mentorProfileData}
                      emrSectionsApiSignature={emrSectionsApiSignature}
                      emrSettingsSaveApiSignature={emrSettingsSaveApiSignature}
                      hideReportsTabsRelatedActions={forSupportPortal}
                    />
                  </div>
                </div>
              )}
            {selected === EMR_SETTINGS_SECTIONS.appointmentCalendarSettings.id &&
              !excludeSections.includes(EMR_SETTINGS_SECTIONS.appointmentCalendarSettings.id) && (
                <div>
                  <div style={{ padding: '32px 8px', width: '100%' }}>
                    <AppointmentCalendarSettings
                      appointmentCalendarSettingsGetApiSignature={
                        appointmentCalendarSettingsGetApiSignature
                      }
                      appointmentCalendarSettingsSaveApiSignature={
                        appointmentCalendarSettingsSaveApiSignature
                      }
                    />
                  </div>
                </div>
              )}
            {selected === EMR_SETTINGS_SECTIONS.debug.id &&
              !excludeSections.includes(EMR_SETTINGS_SECTIONS.debug.id) && (
                <div>
                  <div style={{ padding: '32px 8px', width: '100%' }}>
                    <div>
                      <button
                        className="btn btn-primary"
                        style={{ margin: '32px 0' }}
                        onClick={() => {
                          const debugKey = localStorage.getItem('token')
                          if (debugKey) {
                            navigator.clipboard.writeText(debugKey)
                            toast.success('Debug key copied to clipboard.')
                          } else {
                            toast.error('No debug key found.')
                          }
                        }}
                      >
                        Copy Debug Key
                      </button>
                    </div>
                  </div>
                </div>
              )}
            {(selected === EMR_SETTINGS_SECTIONS.prescriptionViewSettings.id ||
              selected === EMR_SETTINGS_SECTIONS.printSettings.id) &&
              !excludeSections.includes(EMR_SETTINGS_SECTIONS.prescriptionViewSettings.id) &&
              !excludeSections.includes(EMR_SETTINGS_SECTIONS.printSettings.id) && (
                <div style={{ padding: '8px 8px', width: '100%' }}>
                  <PrescriptionViewSettings
                    settingsType={
                      selected === EMR_SETTINGS_SECTIONS.prescriptionViewSettings.id
                        ? PRESCRIPTION_VIEW_SETTINGS_TYPE.editorSettings
                        : PRESCRIPTION_VIEW_SETTINGS_TYPE.printSettings
                    }
                    mentorProfileData={mentorProfileData}
                    prescriptionViewSettingsGetApiSignature={
                      prescriptionViewSettingsGetApiSignature
                    }
                    prescriptionViewSettingsSaveApiSignature={
                      prescriptionViewSettingsSaveApiSignature
                    }
                  />
                </div>
              )}

            {selected === EMR_SETTINGS_SECTIONS.supportOnlyDoctorSettings.id &&
              !excludeSections.includes(EMR_SETTINGS_SECTIONS.supportOnlyDoctorSettings.id) && (
                <div>
                  <div style={{ padding: '32px 8px', width: '100%' }}>
                    <SupportOnlyDoctorSettings practitionerId={practitionerId} mentorProfileData={mentorProfileData} />
                  </div>
                </div>
              )}

            {selected === EMR_SETTINGS_SECTIONS.assessmentAccess.id &&
              !excludeSections.includes(EMR_SETTINGS_SECTIONS.assessmentAccess.id) && (
                <div>
                  <div style={{ padding: '32px 8px', width: '100%' }}>
                    {forSupportPortal ? (
                      <AssessmentAccessSettings
                        assessmentAccessGetApiSignature={assessmentAccessGetApiSignature}
                        assessmentAccessSaveApiSignature={assessmentAccessSaveApiSignature}
                      />
                    ) : (
                      <PractitionerAssessmentAccessView />
                    )}
                  </div>
                </div>
              )}

            {/* Other UIs conditional but independent */}
          </div>
        )}
      </div>
    </>
  )
}
